import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormControl, MenuItem, Select, Button, Typography, Paper, useMediaQuery, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { downloadFilesAsync } from '../../services/FileDownloader';
import { userHasAccess } from '../../services/RoleBasedAccess';
import { useAssetsContext } from '../../context/AssetsContext';
import { useSession } from '../../context/SessionContext';
import { useAssetActions } from '../../hooks/asset/useAssetActions';
import { RedriveType } from '../../api/useAssetApi';
import { Asset } from '../../state/types';
import { buildAssetIdClipboardText, uniqueAssetIds } from './assetIdClipboard';

const formatOperationLabel = (operation: string) => {
  switch (operation) {
    case 'archive':
      return 'archive';
    case 'unarchive':
      return 'unarchive';
    case 'syndication':
      return 'send to syndication';
    case 'removeRedrive':
      return 'remove from redrive for';
    case 'thumbnails':
      return 'regenerate thumbnails for';
    case 'retryStuckStep':
      return 'retry the stuck step for';
    case 'exterior_bgr':
      return 'reprocess background for';
    case 'cropping':
      return 'reprocess cropped images for';
    case 'featured':
      return 'reprocess featured images for';
    case 'bright_river':
      return 'reprocess with Bright River for';
    default:
      return `trigger ${operation} for`;
  }
};

const buildBatchResultMessage = (
  operation: string,
  counts: { started: number; locked: number; failed: number; duplicate: number }
) => {
  const action = formatOperationLabel(operation);
  const parts: string[] = [];

  if (counts.started > 0) {
    parts.push(`${counts.started} ${counts.started === 1 ? 'asset was' : 'assets were'} queued to ${action}.`);
  }
  if (counts.locked > 0) {
    parts.push(`${counts.locked} ${counts.locked === 1 ? 'asset is' : 'assets are'} currently locked.`);
  }
  if (counts.failed > 0) {
    parts.push(`${counts.failed} ${counts.failed === 1 ? 'asset failed' : 'assets failed'} to start.`);
  }
  if (counts.duplicate > 0) {
    parts.push(`${counts.duplicate} duplicate ${counts.duplicate === 1 ? 'request was' : 'requests were'} skipped.`);
  }

  return parts.join(' ');
};

const normalizeSyndicationName = (value?: string) =>
  (value || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

// Substring (of the normalized syndication step name) -> redriveType the backend
// expects. Order is priority: the first syndicator found across an asset's steps
// wins. 'crs' is checked last so an Auction Edge step (which coexists with a CRS
// step in the same pipeline) is reported as 'auctionedge' rather than 'crs'.
const REDRIVE_TYPE_MATCHERS: ReadonlyArray<[needle: string, type: RedriveType]> = [
  ['mashery', 'mashery'],
  ['auctionedge', 'auctionedge'],
  ['homenet', 'homenet'],
  ['vincue', 'vincue'],
  ['reynolds', 'reynolds'],
  ['crs', 'crs'],
];

const getAssetRedriveType = (asset: Asset): RedriveType | null => {
  const candidates = [
    asset.syndicationCurrentStep,
    asset.syndicationSteps?.[0],
    ...((asset as Asset & Record<string, unknown>).syndicationSteps || []),
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map(normalizeSyndicationName);

  for (const [needle, type] of REDRIVE_TYPE_MATCHERS) {
    if (candidates.some((value) => value.includes(needle))) {
      return type;
    }
  }

  return null;
};

const AssetOperations: React.FC = () => {
  const { selectedAssets, setSelectedAssets, refetch } = useAssetsContext();
  const { user, systems } = useSession();
  const { reprocessAsset, secondaryProcessAsset, removeAssetRedrive, retryStuckStep } = useAssetActions();
  const [selectedOperation, setSelectedOperation] = useState('');
  // null = idle; a number = "Copied N" confirmation on the button itself.
  const [copiedCount, setCopiedCount] = useState<number | null>(null);
  const role = user?.role || '';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onAssetsUpdate = () => {
    setSelectedAssets([]);
    refetch();
  };

  const executeOperation = async () => {
    if (!selectedOperation) return;

    try {
      if (selectedOperation === 'downloadImages') {
        selectedAssets.forEach((asset) => downloadFilesAsync(asset, { systems }));
      } else if (selectedOperation === 'secondaryProcess') {
        selectedAssets.forEach((asset) => {
          secondaryProcessAsset(asset.pk, true);
        });
        alert(`Triggered secondary processing for ${selectedAssets.length} assets.`);
      } else if (selectedOperation === 'removeRedrive') {
        const eligibleAssets = selectedAssets
          .map((asset) => ({ asset, redriveType: getAssetRedriveType(asset) }))
          .filter(
            (entry): entry is { asset: Asset; redriveType: RedriveType } =>
              entry.redriveType !== null
          );

        const skippedCount = selectedAssets.length - eligibleAssets.length;
        const results = await Promise.all(
          eligibleAssets.map(({ asset, redriveType }) =>
            removeAssetRedrive(asset.pk, redriveType, asset.sk, true)
          )
        );

        const startedCount = results.filter((result) => result.status === 'started').length;
        const duplicateCount = results.filter((result) => result.status === 'duplicate').length;
        const failedCount = results.filter((result) => result.status === 'failed').length;

        const messageParts: string[] = [];
        if (startedCount > 0) {
          messageParts.push(
            `${startedCount} ${startedCount === 1 ? 'asset was' : 'assets were'} removed from redrive.`
          );
        }
        if (failedCount > 0) {
          messageParts.push(
            `${failedCount} ${failedCount === 1 ? 'asset failed' : 'assets failed'} to remove from redrive.`
          );
        }
        if (duplicateCount > 0) {
          messageParts.push(
            `${duplicateCount} duplicate ${duplicateCount === 1 ? 'request was' : 'requests were'} skipped.`
          );
        }
        if (skippedCount > 0) {
          messageParts.push(
            `${skippedCount} ${skippedCount === 1 ? 'asset was' : 'assets were'} skipped because the syndicator is not supported for redrive removal.`
          );
        }

        refetch();
        alert(messageParts.join(' ') || 'No redrive removals were sent.');
      } else if (selectedOperation === 'retryStuckStep') {
        const username = user?.username;
        if (!username) {
          alert('Unable to determine the current user. Please sign in again.');
          return;
        }

        const results = await Promise.all(
          selectedAssets.map((asset) => retryStuckStep(asset.pk, username, true))
        );

        const counts = results.reduce(
          (acc, result) => {
            acc[result.status] += 1;
            return acc;
          },
          { started: 0, locked: 0, failed: 0, duplicate: 0 }
        );

        refetch();
        const message = buildBatchResultMessage(selectedOperation, counts);
        alert(message || `No ${selectedOperation} requests were sent.`);
      } else {
        const results = await Promise.all(
          selectedAssets.map((asset) =>
            reprocessAsset(asset.pk, asset.sk, selectedOperation, { notify: false })
          )
        );

        const counts = results.reduce(
          (acc, result) => {
            acc[result.status] += 1;
            return acc;
          },
          { started: 0, locked: 0, failed: 0, duplicate: 0 }
        );

        if (selectedOperation === 'archive' || selectedOperation === 'unarchive') {
          onAssetsUpdate();
        }

        const message = buildBatchResultMessage(selectedOperation, counts);
        if (message) {
          alert(message);
        } else {
          alert(`No ${selectedOperation} requests were sent.`);
        }
      }
    } finally {
      setSelectedAssets([]);
      setSelectedOperation('');
    }
  };

  const uniqueSelectedAssetIds = useMemo(
    () => uniqueAssetIds(selectedAssets),
    [selectedAssets]
  );

  // Reset the "Copied N" confirmation whenever the selection changes, so it never
  // reports a count that no longer matches what is checked. selectedAssets comes
  // straight from useState, so this fires on real changes rather than every render.
  useEffect(() => setCopiedCount(null), [selectedAssets]);

  // ...and revert on its own, so the button goes back to advertising what it does.
  useEffect(() => {
    if (copiedCount === null) return;
    const timer = setTimeout(() => setCopiedCount(null), 2000);
    return () => clearTimeout(timer);
  }, [copiedCount]);

  const handleCopyAssetIds = async () => {
    if (uniqueSelectedAssetIds.length === 0) return;
    const payload = buildAssetIdClipboardText(selectedAssets);
    try {
      await navigator.clipboard.writeText(payload);
      setCopiedCount(uniqueSelectedAssetIds.length);
    } catch (error) {
      console.error('Failed to copy asset IDs:', error);
      alert('Could not copy to clipboard.');
    }
  };

  if (selectedAssets.length === 0) return null;

  return (
    <Paper sx={{ padding: 2, borderRadius: 2, marginBottom: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Asset Operations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            data-cy="asset-ops-copy-ids-btn"
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopyAssetIds}
            sx={{
              textTransform: 'none',
              color: theme.palette.text.secondary,
              borderColor: theme.palette.divider,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.text.secondary,
              }
            }}
          >
            {copiedCount === null ? `Copy IDs (${uniqueSelectedAssetIds.length})` : `Copied ${copiedCount}`}
          </Button>
          <Button
            data-cy="asset-ops-clear-btn"
            variant="outlined"
            size="small"
            onClick={() => setSelectedAssets([])}
            sx={{
              textTransform: 'none',
              color: theme.palette.text.secondary,
              borderColor: theme.palette.divider,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.text.secondary,
              }
            }}
          >
            Clear ({selectedAssets.length})
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1.5 : 0 }}>
        <FormControl sx={{ width: isMobile ? '100%' : '50%', mr: isMobile ? 0 : 2 }}>
          <Select
            data-cy="asset-ops-operation-select"
            value={selectedOperation}
            onChange={(e) => setSelectedOperation(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="" disabled>Select an Operation...</MenuItem>
            <MenuItem value="downloadImages">Download Images</MenuItem>
            {userHasAccess(role, ['Internal']) && [
              <MenuItem value="cropping" key="cropping">Reprocess Cropped</MenuItem>,
              <MenuItem value="featured" key="featured">Reprocess Featured</MenuItem>,
              <MenuItem value="exterior_bgr" key="exterior_bgr">Reprocess Background</MenuItem>,
              <MenuItem value="bright_river" key="bright_river">Reprocess Bright River</MenuItem>,
              <MenuItem value="thumbnails" key="thumbnails">Regenerate Thumbnails</MenuItem>,
              <MenuItem value="retryStuckStep" key="retryStuckStep">Retry Stuck Step</MenuItem>
            ]}
            {userHasAccess(role, ['Internal', 'Client Plus']) && (
              <MenuItem value="secondaryProcess" key="secondaryProcess">Secondary Process</MenuItem>
            )}
            {userHasAccess(role, ['Internal', 'Client Plus']) && (
              <MenuItem value="syndication">Send to Syndication</MenuItem>
            )}
            {userHasAccess(role, ['Internal']) && (
              <MenuItem value="removeRedrive">Remove from Redrive</MenuItem>
            )}
            {userHasAccess(role, ['Internal']) && (
              <MenuItem value="archive" key="archive">Archive</MenuItem>
            )}
            {selectedAssets.some((asset) => !!asset.archived) && userHasAccess(role, ['Internal']) && (
              <MenuItem value="unarchive">Unarchive</MenuItem>
            )}
          </Select>
        </FormControl>

        <Button
          data-cy="asset-ops-execute-btn"
          variant="contained"
          color="warning"
          onClick={executeOperation}
          disabled={!selectedOperation}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          Execute
        </Button>
      </Box>
    </Paper>
  );
};

export default AssetOperations;
